<template>
  <DeepDialog
    v-model="show"
    :title="t('wish_ticket.title')"
    max-width="1000"
    prevent-close-on-click-outside
  >
    <template #content>
      <v-form
        ref="form"
        v-model="isFormValid"
        class="wish-ticket-form"
        :validate-on="isFormValid === undefined ? 'submit' : 'input'"
      >
        <v-row>
          <v-col cols="12" md="6">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.email')"
              required
            >
              <v-text-field
                :id
                v-model="model.email"
                type="email"
                hide-details="auto"
                :rules="[ruleRequired, ruleEmail]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12" md="6">
            <FormLabelField v-slot="{ id }" :label="t('application')" required>
              <v-autocomplete
                :id
                v-model="model.application"
                :items="applications"
                :loading="isApplicationsLoading"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12" md="6">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.customer_name')"
              required
            >
              <v-text-field
                :id
                v-model="model.customer_name"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12" md="6">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.customer_license')"
            >
              <v-text-field
                :id
                v-model="model.customer_license"
                hide-details="auto"
              />
            </FormLabelField>
          </v-col>

          <v-col cols="12">
            <h3 class="text-title-medium mt-4">
              {{ t('wish_ticket.section_description') }}
            </h3>
          </v-col>

          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.description')"
              required
            >
              <v-textarea
                :id
                v-model="model.description"
                rows="4"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.use_cases')"
              required
            >
              <v-textarea
                :id
                v-model="model.use_cases"
                rows="4"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.frequency.label')"
              required
            >
              <v-select
                :id
                v-model="model.frequency"
                :items="frequencyItems"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.target_audience')"
              required
            >
              <v-textarea
                :id
                v-model="model.target_audience"
                rows="4"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.benefit')"
              required
            >
              <v-textarea
                :id
                v-model="model.benefit"
                rows="4"
                hide-details="auto"
                :rules="[ruleRequired]"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.discussed_with_abacus')"
              required
            >
              <v-radio-group
                :id
                v-model="model.discussed_with_abacus"
                inline
                hide-details="auto"
                :rules="[(v) => ruleRequired(v, { allowFalse: true })]"
              >
                <v-radio :label="t('wish_ticket.yes')" :value="true" />
                <v-radio :label="t('wish_ticket.no')" :value="false" />
              </v-radio-group>
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.additional_info')"
            >
              <v-textarea
                :id
                v-model="model.additional_info"
                rows="4"
                hide-details="auto"
              />
            </FormLabelField>
          </v-col>
          <v-col cols="12">
            <FormLabel :aria-label="t('attachments')">
              {{ t('attachments') }}
            </FormLabel>
            <DeepDropzoneUploader
              ref="uploader"
              v-model="files"
              multiple
              clearable
              browse-text-color="secondary"
              show-file-icon
              :max-files-size="3000000"
              :accept-mime-types="[]"
            />
          </v-col>
          <v-col cols="12">
            <FormLabelField
              v-slot="{ id }"
              :label="t('wish_ticket.privacy')"
              required
            >
              <v-checkbox
                :id
                v-model="model.privacy_accepted"
                color="primary"
                hide-details="auto"
                :rules="[ruleRequired]"
              >
                <template #label>
                  <i18n-t keypath="wish_ticket.privacy_text" tag="span">
                    <template #link>
                      <a
                        :href="PRIVACY_POLICY_URL"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary"
                        @click.stop
                      >
                        {{ t('wish_ticket.privacy_link') }}
                      </a>
                    </template>
                  </i18n-t>
                </template>
              </v-checkbox>
            </FormLabelField>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template #actions>
      <v-btn-secondary @click="show = false">
        {{ t('actions.cancel') }}
      </v-btn-secondary>
      <v-btn-primary :loading="sendLoading" @click="submit">
        {{ t('wish_ticket.submit') }}
      </v-btn-primary>
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import { toast } from 'vue-sonner'
import { fetchWishApplications, postWishTicket } from '@/api/wish-ticket'
import { WISH_TICKET_FREQUENCIES } from '@/api/wish-ticket/types'
import { computedAsync } from '@vueuse/core'
import { DeepDialog, DeepDropzoneUploader } from '@deepcloud/deep-ui-lib'
import { ruleEmail, ruleRequired } from '@deepcloud/field-validators'
import { useAuth } from '@/composables/use-auth'
import FormLabel from '@/components/common/FormLabel.vue'
import FormLabelField from '@/components/common/FormLabelField.vue'
import type { SupportedLanguage } from '@/api/types'
import type {
  WishTicketFrequency,
  WishTicketPostRequest,
} from '@/api/wish-ticket/types'
import type { DropzoneUploaderItem } from '@deepcloud/deep-ui-lib'

type WishTicketModel = Omit<
  WishTicketPostRequest,
  'frequency' | 'discussed_with_abacus' | 'lang'
> & {
  frequency: WishTicketFrequency | undefined
  discussed_with_abacus: boolean | undefined
}

const PRIVACY_POLICY_URL = 'https://www.abacus.ch/datenschutz'

const { t, locale } = useI18n()
const { user } = useAuth()
const isApplicationsLoading = ref(false)
const applications = computedAsync(fetchWishApplications, [], {
  evaluating: isApplicationsLoading,
  lazy: true,
})

const show = defineModel<boolean>()

const form = useTemplateRef('form')
const uploader = useTemplateRef('uploader')
const isFormValid = ref<boolean>()
const sendLoading = ref(false)
const model = ref<WishTicketModel>(createEmptyModel())
const files = ref<DropzoneUploaderItem[]>([])

const frequencyItems = computed(() =>
  Object.values(WISH_TICKET_FREQUENCIES).map((value) => ({
    title: t(`wish_ticket.frequency.${value}`),
    value,
  })),
)

function createEmptyModel(): WishTicketModel {
  return {
    email: user.value?.profile?.email ?? '',
    application: '',
    customer_name: '',
    customer_license: '',
    description: '',
    use_cases: '',
    frequency: undefined,
    target_audience: '',
    benefit: '',
    discussed_with_abacus: undefined,
    additional_info: '',
    privacy_accepted: false,
  }
}

watch(show, (isOpen) => {
  if (!isOpen) return

  model.value = createEmptyModel()
  files.value = []
  isFormValid.value = undefined
})

async function submit() {
  const result = await form.value?.validate()
  if (!result?.valid || !uploader.value?.isValid) {
    toast.error(t('errors.check_your_inputs'))
    return
  }

  sendLoading.value = true
  try {
    await postWishTicket(
      {
        ...model.value,
        lang: locale.value as SupportedLanguage,
      } as WishTicketPostRequest,
      files.value.map((item) => item.file),
    )
    toast.success(t('wish_ticket.sent'))
    show.value = false
  } catch {
    toast.error(t('errors.error_occurred'))
  } finally {
    sendLoading.value = false
  }
}
</script>

<style scoped>
.wish-ticket-form :deep(.form-label) {
  font-size: 13px;
}
</style>
